import React, { useState, useEffect, useMemo, useCallback } from "react";
import client from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { telHref, waHref, mailHref } from "../../mock";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "../../components/ui/sheet";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";
import {
  Search, MapPin, Phone, MessageCircle, Mail, Globe, Instagram, ShieldCheck,
  Plus, Pencil, Trash2, Upload, BookMarked, Building2, Loader2, ExternalLink, Info,
} from "lucide-react";
import { toast } from "sonner";
import { tr } from "../../i18n/LangContext";

const TIER_STYLE = {
  preferred: "bg-brand-green text-white",
  trusted: "border border-brand-green/50 text-brand-green",
  conditional: "border border-white/20 text-muted-foreground",
};

const EMPTY = {
  name: "", company: "", category: "", sub_specialisms: "", cities: "", languages: "",
  mb_tier: "trusted", verified: false, public_note: "", internal_note: "",
  phone: "", whatsapp: "", email: "", address: "", maps_url: "", website: "", instagram: "",
};

const toArr = (s) => (s || "").split(/[;,]/).map((x) => x.trim()).filter(Boolean);
const toStr = (a) => (Array.isArray(a) ? a.join(", ") : (a || ""));

export default function BlackBook() {
  const { user, isSuper } = useAuth();
  const [scope, setScope] = useState("global"); // global | private
  const [global, setGlobal] = useState([]);
  const [priv, setPriv] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [tiers, setTiers] = useState([]);

  const [query, setQuery] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [cityFilter, setCityFilter] = useState("all");
  const [tierFilter, setTierFilter] = useState("all");
  const [verifiedOnly, setVerifiedOnly] = useState(false);

  const [detail, setDetail] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null); // contact being edited, or null for new
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState("");
  const [importing, setImporting] = useState(false);
  const [delTarget, setDelTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [g, p, m] = await Promise.all([
        client.get("/contacts", { params: { scope: "global" } }),
        client.get("/contacts", { params: { scope: "private" } }),
        client.get("/contacts/meta"),
      ]);
      setGlobal(g.data);
      setPriv(p.data);
      setCategories(m.data.categories || []);
      setTiers(m.data.tiers || []);
    } catch (e) {
      toast.error(tr("Couldn't load the Black Book"));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const base = scope === "global" ? global : priv;
  const cityOptions = useMemo(
    () => Array.from(new Set(base.flatMap((c) => c.cities || []).filter(Boolean))).sort(),
    [base]
  );

  const items = useMemo(() => {
    const q = query.trim().toLowerCase();
    return base.filter((c) => {
      const matchQ = !q || [c.name, c.company, c.category, (c.sub_specialisms || []).join(" "), (c.cities || []).join(" ")]
        .join(" ").toLowerCase().includes(q);
      const matchCat = catFilter === "all" || c.category === catFilter;
      const matchCity = cityFilter === "all" || (c.cities || []).includes(cityFilter);
      const matchTier = tierFilter === "all" || c.mb_tier === tierFilter;
      const matchVer = !verifiedOnly || c.verified;
      return matchQ && matchCat && matchCity && matchTier && matchVer;
    });
  }, [base, query, catFilter, cityFilter, tierFilter, verifiedOnly]);

  const canEditInstitutional = isSuper;

  const openView = (c) => {
    setDetail(c);
    client.post(`/contacts/${c.id}/view`).catch(() => {});
  };

  const openNew = (asScope) => {
    setEditing(null);
    setForm({ ...EMPTY, verified: asScope === "global" });
    setFormScope(asScope);
    setFormOpen(true);
  };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name || "", company: c.company || "", category: c.category || "",
      sub_specialisms: toStr(c.sub_specialisms), cities: toStr(c.cities), languages: toStr(c.languages),
      mb_tier: c.mb_tier || "trusted", verified: !!c.verified,
      public_note: c.public_note || "", internal_note: c.internal_note || "",
      phone: c.phone || "", whatsapp: c.whatsapp || "", email: c.email || "",
      address: c.address || "", maps_url: c.maps_url || "", website: c.website || "", instagram: c.instagram || "",
    });
    setFormScope(c.owner_scope);
    setFormOpen(true);
  };
  const [formScope, setFormScope] = useState("private");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error(tr("Name is required")); return; }
    setSaving(true);
    const payload = {
      owner_scope: formScope,
      name: form.name.trim(), company: form.company.trim(), category: form.category,
      sub_specialisms: toArr(form.sub_specialisms), cities: toArr(form.cities), languages: toArr(form.languages),
      mb_tier: form.mb_tier, verified: !!form.verified,
      public_note: form.public_note, internal_note: form.internal_note,
      phone: form.phone.trim(), whatsapp: form.whatsapp.trim(), email: form.email.trim(),
      address: form.address.trim(), maps_url: form.maps_url.trim(), website: form.website.trim(), instagram: form.instagram.trim(),
    };
    try {
      if (editing) {
        const res = await client.put(`/contacts/${editing.id}`, payload);
        const upd = (arr) => arr.map((x) => (x.id === editing.id ? res.data : x));
        editing.owner_scope === "global" ? setGlobal(upd) : setPriv(upd);
        if (detail?.id === editing.id) setDetail(res.data);
        toast.success(tr("Contact updated"));
      } else {
        const res = await client.post("/contacts", payload);
        formScope === "global" ? setGlobal((a) => [res.data, ...a]) : setPriv((a) => [res.data, ...a]);
        toast.success(tr("Contact added"));
      }
      setFormOpen(false);
    } catch (err) {
      toast.error(tr("Couldn't save contact"), { description: err?.response?.data?.detail });
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    const c = delTarget;
    setDelTarget(null);
    try {
      await client.delete(`/contacts/${c.id}`);
      const rm = (arr) => arr.filter((x) => x.id !== c.id);
      c.owner_scope === "global" ? setGlobal(rm) : setPriv(rm);
      if (detail?.id === c.id) setDetail(null);
      toast.success(tr("Contact deleted"));
    } catch (err) {
      toast.error(tr("Couldn't delete"), { description: err?.response?.data?.detail });
    }
  };

  const doImport = async () => {
    if (!importText.trim()) { toast.error(tr("Paste CSV text first")); return; }
    setImporting(true);
    try {
      const res = await client.post("/contacts/import", { csv_text: importText, mark_verified: true });
      toast.success(`Imported ${res.data.created} contacts`, { description: `${res.data.total_global} total in the Black Book` });
      setImportOpen(false);
      setImportText("");
      load();
    } catch (err) {
      toast.error(tr("Import failed"), { description: err?.response?.data?.detail });
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="grid h-10 w-10 place-items-center rounded-xl bg-brand-green/15 text-brand-green"><BookMarked className="h-5 w-5" /></span>
          <div>
            <h2 className="font-display text-lg font-semibold text-white">{tr("Black Book")}</h2>
            <p className="text-xs text-muted-foreground">{tr("Your vetted directory of contacts across Morocco")}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {scope === "global" && canEditInstitutional && (
            <>
              <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="bb-import-btn"
                className="h-10 border-white/12 bg-white/[0.03] text-white hover:bg-white/[0.07]"><Upload className="h-4 w-4" /> {tr("Import CSV")}</Button>
              <Button onClick={() => openNew("global")} data-testid="bb-add-institutional-btn"
                className="h-10 bg-brand-green hover:bg-brand-greenDark text-white"><Plus className="h-4 w-4" /> {tr("Add institutional")}</Button>
            </>
          )}
          {scope === "private" && (
            <Button onClick={() => openNew("private")} data-testid="bb-add-contact-btn"
              className="h-10 bg-brand-red hover:bg-brand-redDark text-white"><Plus className="h-4 w-4" /> {tr("Add my own contact")}</Button>
          )}
        </div>
      </div>

      {/* Segmented switcher */}
      <div className="inline-flex rounded-xl border border-white/10 bg-white/[0.02] p-1">
        <button onClick={() => setScope("global")} data-testid="bb-tab-institutional"
          className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${scope === "global" ? "bg-brand-green/15 text-brand-green" : "text-muted-foreground hover:text-white"}`}>
          <ShieldCheck className="h-4 w-4" /> {tr("Institutional")} <span className="rounded-full bg-white/8 px-1.5 text-xs">{global.length}</span>
        </button>
        <button onClick={() => setScope("private")} data-testid="bb-tab-private"
          className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${scope === "private" ? "bg-white/10 text-white" : "text-muted-foreground hover:text-white"}`}>
          <Building2 className="h-4 w-4" /> {tr("My Contacts")} <span className="rounded-full bg-white/8 px-1.5 text-xs">{priv.length}</span>
        </button>
      </div>

      {/* Search + filters */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="bb-search"
            placeholder={tr("Search name, company, category, city…")} className="h-11 bg-input/50 pl-10 border-white/10 focus-visible:ring-brand-green" />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={catFilter} onValueChange={setCatFilter}>
            <SelectTrigger className="h-11 w-[190px] border-white/12 bg-white/[0.03] text-white" data-testid="bb-filter-category"><SelectValue /></SelectTrigger>
            <SelectContent className="glass-strong border-white/10 max-h-72">
              <SelectItem value="all">{tr("All categories")}</SelectItem>
              {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={cityFilter} onValueChange={setCityFilter}>
            <SelectTrigger className="h-11 w-[170px] border-white/12 bg-white/[0.03] text-white" data-testid="bb-filter-city">
              <MapPin className="h-4 w-4 text-muted-foreground" /><SelectValue />
            </SelectTrigger>
            <SelectContent className="glass-strong border-white/10 max-h-72">
              <SelectItem value="all">{tr("All cities")}</SelectItem>
              {cityOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={tierFilter} onValueChange={setTierFilter}>
            <SelectTrigger className="h-11 w-[140px] border-white/12 bg-white/[0.03] text-white" data-testid="bb-filter-tier"><SelectValue /></SelectTrigger>
            <SelectContent className="glass-strong border-white/10">
              <SelectItem value="all">{tr("All tiers")}</SelectItem>
              {tiers.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <button onClick={() => setVerifiedOnly((v) => !v)} data-testid="bb-verified-toggle"
            className={`flex h-11 items-center gap-1.5 rounded-md border px-3 text-sm font-medium transition-colors ${verifiedOnly ? "border-brand-green bg-brand-green/15 text-brand-green" : "border-white/12 bg-white/[0.03] text-muted-foreground hover:text-white"}`}>
            <ShieldCheck className="h-4 w-4" /> {tr("Verified")}
          </button>
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="grid h-48 place-items-center"><Loader2 className="h-8 w-8 animate-spin text-brand-green" /></div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" data-testid="bb-grid">
          {items.map((c) => {
            const isGlobal = c.owner_scope === "global";
            return (
              <div key={c.id} data-testid="bb-card"
                className={`group flex flex-col rounded-2xl border border-white/[0.06] bg-card/40 p-4 transition-all hover:-translate-y-0.5 hover:border-white/15 ${isGlobal ? "border-l-4 border-l-brand-green" : "border-l-4 border-l-[#919396]"}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate font-display text-sm font-semibold text-white">{c.name}</p>
                    {c.company && <p className="truncate text-xs text-muted-foreground">{c.company}</p>}
                  </div>
                  {isGlobal ? (
                    c.verified && <span className="flex shrink-0 items-center gap-1 rounded-full bg-brand-green/15 px-2 py-0.5 text-[10px] font-semibold text-brand-green"><ShieldCheck className="h-3 w-3" /> {tr("MB Verified")}</span>
                  ) : (
                    <span className="shrink-0 rounded-full border border-[#919396]/50 px-2 py-0.5 text-[10px] font-semibold text-[#c7c9cc]">{tr("My Contact")}</span>
                  )}
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  {c.category && <span className="rounded-full bg-brand-green/12 px-2 py-0.5 text-[10px] text-brand-green">{c.category}</span>}
                  {(c.cities || []).slice(0, 2).map((ct) => (
                    <span key={ct} className="inline-flex items-center gap-1 text-[10px] text-muted-foreground"><MapPin className="h-3 w-3" />{ct}</span>
                  ))}
                  {isGlobal && c.mb_tier && <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold capitalize ${TIER_STYLE[c.mb_tier] || ""}`}>{tr(c.mb_tier)}</span>}
                </div>
                {c.public_note && <p className="mt-3 line-clamp-2 text-xs leading-relaxed text-muted-foreground">{c.public_note}</p>}
                <div className="mt-4 flex items-center gap-2">
                  <Button size="sm" onClick={() => openView(c)} data-testid="bb-view-btn"
                    className="flex-1 bg-white/[0.05] text-white hover:bg-white/[0.1]"><ExternalLink className="h-3.5 w-3.5" /> {tr("View")}</Button>
                  {((isGlobal && canEditInstitutional) || !isGlobal) && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => openEdit(c)} data-testid="bb-edit-btn"
                        className="border-white/12 bg-white/[0.03] text-white hover:bg-white/[0.07]"><Pencil className="h-3.5 w-3.5" /></Button>
                      <Button size="sm" variant="outline" onClick={() => setDelTarget(c)} data-testid="bb-delete-btn"
                        className="border-brand-red/30 bg-brand-red/10 text-brand-red hover:bg-brand-red/20"><Trash2 className="h-3.5 w-3.5" /></Button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
          {items.length === 0 && (
            <div className="col-span-full grid h-40 place-items-center rounded-2xl border border-dashed border-white/10 text-sm text-muted-foreground">
              {scope === "private" ? tr("No contacts yet — add your own with the button above.") : tr("No contacts match those filters.")}
            </div>
          )}
        </div>
      )}

      {/* Detail drawer */}
      <Sheet open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <SheetContent side="right" className="w-full overflow-y-auto border-white/10 bg-background sm:max-w-md" data-testid="bb-detail-drawer">
          {detail && (
            <div className="relative">
              {detail.owner_scope === "global" && (
                <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden" data-testid="bb-watermark" aria-hidden>
                  {Array.from({ length: 10 }).map((_, i) => (
                    <p key={i} className="whitespace-nowrap text-white" style={{ opacity: 0.08, transform: "rotate(-30deg)", fontSize: 13, marginTop: 46 }}>
                      {`${user?.name || ""} · ${user?.email || ""} · `.repeat(4)}
                    </p>
                  ))}
                </div>
              )}
              <div className="relative z-10">
                <SheetHeader>
                  <div className="flex items-center gap-2">
                    <SheetTitle className="text-xl text-white">{detail.name}</SheetTitle>
                    {detail.owner_scope === "global" && detail.verified && (
                      <span className="flex items-center gap-1 rounded-full bg-brand-green/15 px-2 py-0.5 text-[10px] font-semibold text-brand-green"><ShieldCheck className="h-3 w-3" /> {tr("MB Verified")}</span>
                    )}
                  </div>
                  {detail.company && <p className="text-sm text-muted-foreground">{detail.company}</p>}
                </SheetHeader>

                <div className="mt-4 flex flex-wrap gap-1.5">
                  {detail.category && <span className="rounded-full bg-brand-green/12 px-2 py-0.5 text-[11px] text-brand-green">{detail.category}</span>}
                  {detail.owner_scope === "global" && detail.mb_tier && <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize ${TIER_STYLE[detail.mb_tier] || ""}`}>{tr(detail.mb_tier)}</span>}
                </div>

                {detail.public_note && (
                  <div className="mt-4">
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{tr("About")}</p>
                    <p className="mt-1 text-sm leading-relaxed text-white/90">{detail.public_note}</p>
                  </div>
                )}
                {detail.internal_note && (
                  <div className="mt-4 rounded-xl border border-white/8 bg-white/[0.02] p-3">
                    <p className="text-[10px] uppercase tracking-widest text-brand-green">{tr("Working note")}</p>
                    <p className="mt-1 text-sm leading-relaxed text-white/80">{detail.internal_note}</p>
                  </div>
                )}
                {(detail.sub_specialisms || []).length > 0 && (
                  <div className="mt-4">
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{tr("Specialisms")}</p>
                    <div className="mt-1 flex flex-wrap gap-1.5">{detail.sub_specialisms.map((s) => <span key={s} className="rounded-md bg-white/5 px-2 py-0.5 text-xs text-white/80">{s}</span>)}</div>
                  </div>
                )}
                <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
                  {(detail.cities || []).length > 0 && <div><p className="text-[10px] uppercase tracking-widest text-muted-foreground">{tr("Cities")}</p><p className="mt-1 text-white/90">{detail.cities.join(", ")}</p></div>}
                  {(detail.languages || []).length > 0 && <div><p className="text-[10px] uppercase tracking-widest text-muted-foreground">{tr("Languages")}</p><p className="mt-1 text-white/90">{detail.languages.join(", ")}</p></div>}
                </div>

                {/* Contact block */}
                <div className="mt-5 space-y-2">
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{tr("Contact")}</p>
                  <DetailRow icon={Phone} label={tr("Phone")} value={detail.phone} href={telHref(detail.phone)} />
                  <DetailRow icon={MessageCircle} label={tr("WhatsApp")} value={detail.whatsapp} href={waHref(detail.whatsapp || detail.phone)} tone="green" />
                  <DetailRow icon={Mail} label={tr("Email")} value={detail.email} href={mailHref(detail.email)} />
                  <DetailRow icon={MapPin} label={tr("Address")} value={detail.address} href={detail.maps_url || null} />
                  <DetailRow icon={Globe} label={tr("Website")} value={detail.website} href={detail.website || null} />
                  <DetailRow icon={Instagram} label={tr("Instagram")} value={detail.instagram} href={detail.instagram || null} />
                </div>

                {detail.edited_by && detail.owner_scope === "private" && (
                  <p className="mt-4 text-[11px] italic text-brand-green">{tr("Edited by the MB team")}</p>
                )}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Add / edit form */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-lg glass-strong border-white/10 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-white">
              {editing ? tr("Edit contact") : formScope === "global" ? tr("Add institutional contact") : tr("Add my own contact")}
            </DialogTitle>
            {!editing && formScope === "private" && (
              <DialogDescription className="flex items-start gap-2 rounded-lg border border-white/8 bg-white/[0.02] p-2.5 text-xs text-muted-foreground">
                <Info className="mt-0.5 h-4 w-4 shrink-0 text-brand-green" />
                {tr("Contacts you add here are stored in your own CRM. The Moroccan Brothers team can see and help maintain them so we can support you in vetting and briefing them — we won't contact them without asking you first.")}
              </DialogDescription>
            )}
          </DialogHeader>
          <form onSubmit={save} className="space-y-3 pt-1">
            <div className="grid grid-cols-2 gap-3">
              <Field label={tr("Name *")}><Input value={form.name} onChange={set("name")} data-testid="contact-form-name" className="bg-input/60 border-white/10" /></Field>
              <Field label={tr("Company")}><Input value={form.company} onChange={set("company")} data-testid="contact-form-company" className="bg-input/60 border-white/10" /></Field>
            </div>
            <Field label={tr("Category")}>
              <Select value={form.category} onValueChange={set("category")}>
                <SelectTrigger className="bg-input/60 border-white/10" data-testid="contact-form-category"><SelectValue placeholder={tr("Select category")} /></SelectTrigger>
                <SelectContent className="glass-strong border-white/10 max-h-64">
                  {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label={tr("Cities (comma separated)")}><Input value={form.cities} onChange={set("cities")} placeholder={tr("Marrakech, Casablanca")} className="bg-input/60 border-white/10" /></Field>
              <Field label={tr("Languages (comma separated)")}><Input value={form.languages} onChange={set("languages")} placeholder={tr("French, Arabic")} className="bg-input/60 border-white/10" /></Field>
            </div>
            <Field label={tr("Sub-specialisms (comma separated)")}><Input value={form.sub_specialisms} onChange={set("sub_specialisms")} placeholder={tr("riad restoration, tadelakt")} className="bg-input/60 border-white/10" /></Field>
            {formScope === "global" && (
              <div className="grid grid-cols-2 gap-3">
                <Field label={tr("MB Tier")}>
                  <Select value={form.mb_tier} onValueChange={set("mb_tier")}>
                    <SelectTrigger className="bg-input/60 border-white/10" data-testid="contact-form-tier"><SelectValue /></SelectTrigger>
                    <SelectContent className="glass-strong border-white/10">{tiers.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label={tr("Verified")}>
                  <button type="button" onClick={() => setForm((f) => ({ ...f, verified: !f.verified }))} data-testid="contact-form-verified"
                    className={`flex h-10 w-full items-center justify-center gap-1.5 rounded-md border text-sm font-medium ${form.verified ? "border-brand-green bg-brand-green/15 text-brand-green" : "border-white/12 bg-white/[0.03] text-muted-foreground"}`}>
                    <ShieldCheck className="h-4 w-4" /> {form.verified ? "MB Verified" : "Not verified"}
                  </button>
                </Field>
              </div>
            )}
            <Field label={tr("Public note")}><Textarea value={form.public_note} onChange={set("public_note")} rows={2} placeholder={tr("What they're good at, how to brief them")} className="bg-input/60 border-white/10" /></Field>
            <Field label={formScope === "global" ? tr("Internal / working note (visible to members)") : tr("Working note")}><Textarea value={form.internal_note} onChange={set("internal_note")} rows={2} className="bg-input/60 border-white/10" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label={tr("Phone")}><Input value={form.phone} onChange={set("phone")} className="bg-input/60 border-white/10" /></Field>
              <Field label={tr("WhatsApp")}><Input value={form.whatsapp} onChange={set("whatsapp")} className="bg-input/60 border-white/10" /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label={tr("Email")}><Input value={form.email} onChange={set("email")} className="bg-input/60 border-white/10" /></Field>
              <Field label={tr("Website")}><Input value={form.website} onChange={set("website")} placeholder={tr("https://")} className="bg-input/60 border-white/10" /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label={tr("Address")}><Input value={form.address} onChange={set("address")} className="bg-input/60 border-white/10" /></Field>
              <Field label={tr("Instagram")}><Input value={form.instagram} onChange={set("instagram")} placeholder={tr("https://instagram.com/…")} className="bg-input/60 border-white/10" /></Field>
            </div>
            <Field label={tr("Maps URL")}><Input value={form.maps_url} onChange={set("maps_url")} placeholder={tr("https://maps.google.com/…")} className="bg-input/60 border-white/10" /></Field>
            <DialogFooter className="gap-2 sm:gap-2 pt-1">
              <Button type="button" variant="outline" onClick={() => setFormOpen(false)} className="border-white/12 bg-white/[0.03] text-white hover:bg-white/[0.07]">{tr("Cancel")}</Button>
              <Button type="submit" disabled={saving} data-testid="contact-form-save" className="bg-brand-green hover:bg-brand-greenDark text-white">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : tr("Save contact")}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* CSV import */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-lg glass-strong border-white/10">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-white">{tr("Import institutional contacts")}</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              {tr("Paste CSV with headers: name, company, category, sub_specialisms, cities, languages, mb_tier, verified, public_note, internal_note, phone, whatsapp, email, address, maps_url, website, instagram. Use ; inside multi-value columns. Existing global contacts (same name + company) are skipped.")}
            </DialogDescription>
          </DialogHeader>
          <Textarea value={importText} onChange={(e) => setImportText(e.target.value)} data-testid="bb-import-textarea"
            rows={8} placeholder={tr("name,company,category,…")} className="bg-input/60 border-white/10 font-mono text-xs" />
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setImportOpen(false)} className="border-white/12 bg-white/[0.03] text-white hover:bg-white/[0.07]">{tr("Cancel")}</Button>
            <Button onClick={doImport} disabled={importing} data-testid="bb-import-submit" className="bg-brand-green hover:bg-brand-greenDark text-white">{importing ? <Loader2 className="h-4 w-4 animate-spin" /> : "Import"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <DialogContent className="max-w-sm glass-strong border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">{tr("Delete contact?")}</DialogTitle>
            <DialogDescription className="text-muted-foreground">{tr("This removes")} <span className="text-white">{delTarget?.name}</span>{delTarget?.owner_scope === "global" ? ` ${tr("from the Black Book for everyone.")}` : ` ${tr("from your contacts.")}`}</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setDelTarget(null)} className="border-white/12 bg-white/[0.03] text-white hover:bg-white/[0.07]">{tr("Cancel")}</Button>
            <Button onClick={doDelete} data-testid="bb-delete-confirm" className="bg-brand-red hover:bg-brand-redDark text-white">{tr("Delete")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div><Label className="text-[11px] text-muted-foreground">{label}</Label><div className="mt-1">{children}</div></div>
);

const DetailRow = ({ icon: Icon, label, value, href, tone }) => {
  if (!value) return null;
  const inner = (
    <div className="flex items-center gap-2.5 rounded-lg border border-white/8 bg-white/[0.02] px-3 py-2">
      <Icon className={`h-4 w-4 shrink-0 ${tone === "green" ? "text-brand-green" : "text-muted-foreground"}`} />
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</p>
        <p className="truncate text-sm text-white">{value}</p>
      </div>
    </div>
  );
  return href ? <a href={href} target="_blank" rel="noreferrer" className="block transition-colors hover:border-white/20">{inner}</a> : inner;
};
